//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// email-triage-goals imports
import IGoalsWrapper from "./goals-wrapper.d";
import * as styles from "./goals-wrapper.styles";
import { BaseGoalComponent } from "./base-goal.component";
// other internals
import ReduxState from "../../declarations/state.d";
import { removeGoal, upsertEmailTriage } from "@mutations";
import { standardGoals } from "@util/goal.util";
import { logger } from "@util/logger.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
// material-ui
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
export class EmailTriageGoalComponent extends BaseGoalComponent<IGoalsWrapper.IEmailTriage.State> {
  private handle: number;

  constructor(props: IGoalsWrapper.Props) {
    super(props, standardGoals.EmailTriage);

    this.state = this.calculateStateFromProps(props);

    this.onToggleSyncToGoogle = this.onToggleSyncToGoogle.bind(this);
    this.onNameChanged = this.onNameChanged.bind(this);
    this.onDurationSelected = this.onDurationSelected.bind(this);
    this.onMutationFailure = this.onMutationFailure.bind(this);
  }

  private calculateStateFromProps(_props: IGoalsWrapper.Props): IGoalsWrapper.IEmailTriage.State {
    const goal = this.findGoal();
    const goalOptions = goal && goal.options;

    const withinDayOptions =
      goalOptions && (goalOptions.specificOptions as ISchema.IGoalWithinDayOptions);

    return {
      loading: false,
      selected: !!goal,
      syncToGoogle: (goalOptions && goalOptions.syncToGoogle) || false,
      nameOverride: (goal && goal.name) || "Email Triage",
      durationMinutes: (withinDayOptions && withinDayOptions.idealDuration) || 30,
    };
  }

  private anyErrors(): boolean {
    return !this.state.nameOverride;
  }

  private createEmailTriageOptions(
    newOptions: Partial<IGoalsWrapper.IEmailTriage.State> = this.state,
  ): ISchema.IGoalPatchEmailTriageOptionsInput {
    return {
      minutesDuration: newOptions.durationMinutes,
      nameOverride: newOptions.nameOverride,
      syncToGoogle: newOptions.syncToGoogle,
    };
  }

  // Handlers

  private onUpdatePreferencesSuccess = () => {
    this.props.snackBarOpen("Updated preferences");
  };

  private onUpdatePreferencesFailure = () => {
    this.props.snackBarOpen("An error occurred!");
  };

  private onMutateOnToggleSuccess = (isInputChecked: boolean) => {
    this.setState({ loading: false });
    this.props.snackBarOpen(`${isInputChecked ? "Added" : "Removed"} daily hold`);
  };

  private updatePreferences(newPreferences: Partial<IGoalsWrapper.IEmailTriage.State>): void {
    if (this.anyErrors()) {
      return;
    }

    const options = this.createEmailTriageOptions(newPreferences);
    const org = this.props.org;

    upsertEmailTriage(
      this.props.relay.environment,
      { orgId: org.id, options },
      this.onUpdatePreferencesSuccess,
      this.onUpdatePreferencesFailure,
    );
  }

  protected mutateOnToggle(isInputChecked: boolean): void {
    const org = this.props.org;

    if (isInputChecked) {
      this.setState({ loading: true });
      upsertEmailTriage(
        this.props.relay.environment,
        { orgId: org.id, options: this.createEmailTriageOptions() },
        this.onMutateOnToggleSuccess.bind(this, isInputChecked),
        this.onMutationFailure,
      );
    } else {
      const goal = this.findGoalInput();
      if (goal) {
        this.setState({ loading: true });
        removeGoal(
          this.props.relay.environment,
          { goal, orgId: org.id },
          this.onMutateOnToggleSuccess.bind(this, isInputChecked),
          this.onMutationFailure,
        );
      } else {
        logger.debug("Couldn't find goal to remove");
      }
    }
  }

  private onToggleSyncToGoogle(
    _event: React.ChangeEvent<HTMLInputElement>,
    isInputChecked: boolean,
  ): void {
    const newState = { syncToGoogle: isInputChecked };

    this.setState(newState);
    this.updatePreferences(newState);
  }

  private onNameChanged(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value;
    const newState = { nameOverride: newValue };

    this.setState(newState);

    // wait for 1sec delay after typing before saving
    if (this.handle) {
      window.clearTimeout(this.handle);
    }
    this.handle = window.setTimeout(() => this.updatePreferences(newState), 1000);
  }

  private onDurationSelected(e: React.ChangeEvent<HTMLSelectElement>): void {
    const durationMinutes = parseInt(e.target.value);
    const newState = { durationMinutes };

    this.setState(newState);
    this.updatePreferences(newState);
  }

  private onMutationFailure(): void {
    this.props.snackBarOpen("An error occurred!");
  }

  public componentWillUnmount(): void {
    // TODO: handle case where timer hasn't triggered yet
    window.clearTimeout(this.handle);
  }

  private renderDurationItems(): JSX.Element[] {
    const durationOptions = [30, 60, 90, 120];

    return durationOptions.map((minutes) => (
      <MenuItem key={minutes} value={minutes}>
        {minutes > 60 ? `${minutes / 60} hours` : `${minutes} minutes`}
      </MenuItem>
    ));
  }

  private renderEmailTriageOptions(): JSX.Element {
    const disabled = !this.state.selected || this.state.loading;
    return (
      <div style={styles.container}>
        <FormControl fullWidth error={!this.state.nameOverride}>
          <TextField
            disabled={disabled}
            label="Smart Hold Name"
            value={this.state.nameOverride}
            onChange={this.onNameChanged}
          />
          {!this.state.nameOverride ? (
            <FormHelperText>Smart Hold name must be non-empty</FormHelperText>
          ) : (
            ""
          )}
        </FormControl>
        <br />
        <FormControl>
          <InputLabel shrink htmlFor="cw-smart-hold-duration-select">
            Smart Hold Duration
          </InputLabel>
          <Select
            value={this.state.durationMinutes}
            onChange={this.onDurationSelected}
            name="Smart Hold Duration"
            disabled={disabled}
            inputProps={{
              name: "Smart Hold Duration",
              id: "cw-smart-hold-duration-select",
            }}
          >
            {this.renderDurationItems()}
          </Select>
        </FormControl>
        <div style={styles.checkboxContainer}>
          <FormControl>
            <FormControlLabel
              control={<Checkbox color="primary" checked={this.state.syncToGoogle} />}
              onChange={this.onToggleSyncToGoogle}
              disabled={disabled}
              label="Sync my Smart Holds to my Google Calendar"
              labelPlacement="end"
            />
          </FormControl>
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div style={styles.goal}>
        <div style={styles.toggleContainer}>
          <FormControl>
            <FormControlLabel
              control={<Switch color="primary" checked={this.state.selected} />}
              onChange={this.onToggle}
              disabled={this.state.loading}
              label="Make time for a daily Smart Hold"
              labelPlacement="end"
            />
          </FormControl>
        </div>
        {this.renderEmailTriageOptions()}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: ReduxState.Redux, _ownProps: IGoalsWrapper.Container) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(EmailTriageGoalComponent);

//////////////////
// RELAY
//////////////////
import { fragments } from "./EmailTriageGoal.gql";
export const EmailTriageGoal = createFragmentContainer(reduxContainer, fragments);
